/**
 * Optimized Candidate Builder - Shape→Optimize Pipeline
 *
 * ARCHITECTURE:
 * For each shape in shape registry:
 *   1. Generate millage shape
 *   2. Create and process candidate
 *   3. If viable → Optimize immediately
 *   4. Keep only final optimized version
 *   5. Track optimization history
 *
 * NON-VIABLE HANDLING:
 * When no viable candidates exist, returns the top 3 "closest to viable" candidates
 * (ranked by least negative DRF balance). These can be displayed to help users
 * understand why their configuration isn't working.
 *
 * Also returns all failed candidates separately for super admin debugging.
 */

import { generateSpecificShape } from '~/engine/millage/shape-generators/general-processing';
import { getAllShapeIds } from '~/engine/millage/shape-progression';
import { CashFlowCalculator } from '~/engine/processing/engines/cashflow-engine/drf-calculator';
import { ConfigurationOptimizer } from '~/engine/processing/managers/optimizers/configuration-optimizer';
import { logger } from '~/services/logging';
import { applyMillageOverrides } from '~/services/override-service';
import type { Candidate, Configuration, MillageInformation, Project } from '~/types';
import { createBaseProjectElements, createCandidateFromMillage } from './candidate-builder';

/** Result from buildOptimizedCandidates */
export interface BuildCandidatesResult {
  /** Viable (or closest-to-viable if none viable) candidates */
  candidates: Candidate[];
  /** All non-viable candidates for super admin debugging (ranked by closeness to viable) */
  failedCandidates: Candidate[];
}

/**
 * Maps OptimizationType enum values to OptimizationHistoryEntry type strings
 */
function mapOptimizationTypeToHistoryType(
  optimizationType: string
): 'utilization' | 'five-year-fix' | 'millage-adjustment' | 'target-compliance' {
  if (optimizationType === 'utilization') return 'utilization';
  if (optimizationType === 'five-year-rule') return 'five-year-fix';
  if (optimizationType === 'millage-redistribution') return 'millage-adjustment';
  if (optimizationType === 'target-compliance') return 'target-compliance';
  // Default fallback
  return 'utilization';
}

/** Maximum number of non-viable candidates to return when no viable candidates exist */
const MAX_NON_VIABLE_TO_SHOW = 3;

/**
 * Deep clone a candidate to prevent reference sharing issues
 * This is important when passing candidates to the optimization pipeline,
 * which may create test candidates that share array references
 */
function deepCloneCandidate(candidate: Candidate): Candidate {
  return {
    ...candidate,
    cashFlows: candidate.cashFlows.map((cf) => ({
      ...cf,
      bondPayments: cf.bondPayments.map((bp) => ({ ...bp })),
    })),
    optimizationHistory: candidate.optimizationHistory
      ? [...candidate.optimizationHistory.map((h) => ({ ...h, details: { ...h.details } }))]
      : undefined,
    results: {
      ...candidate.results,
      failureMessages: [...(candidate.results.failureMessages || [])],
      bondSeriesResults: candidate.results.bondSeriesResults.map((bsr) => ({
        ...bsr,
        failureMessages: [...bsr.failureMessages],
      })),
    },
    revenueMap: Object.fromEntries(
      Object.entries(candidate.revenueMap).map(([k, v]) => [
        k,
        { ...v, bondSeriesDetailsByBondId: { ...v.bondSeriesDetailsByBondId } },
      ])
    ),
    millageInformation: {
      ...candidate.millageInformation,
      millageShape: {
        ...candidate.millageInformation.millageShape,
        intervals: [...candidate.millageInformation.millageShape.intervals],
        adjustments: [...candidate.millageInformation.millageShape.adjustments],
      },
      millageMap: { ...candidate.millageInformation.millageMap },
    },
  };
}

/**
 * Creates a custom MillageInformation from millage overrides
 *
 * Uses the baseMillageMap stored in the overrides (captured from the candidate's
 * millage map when the user opened the override modal). Applies the user's
 * override edits on top of this base map.
 *
 * This approach is simple and robust - no need to regenerate from shape IDs.
 * The base map already contains the correct rates, whether from an original
 * shape or from previous customizations.
 */
function createCustomMillageFromOverrides(configuration: Configuration): MillageInformation {
  const millageOverrides = configuration.overrides?.millage;

  if (!millageOverrides) {
    throw new Error('Cannot create custom millage without millage overrides');
  }

  // Get the base millage map stored in overrides
  const baseMillageMap = millageOverrides.baseMillageMap;
  if (!baseMillageMap || Object.keys(baseMillageMap).length === 0) {
    throw new Error(
      'Millage overrides are missing the base millage map. ' +
        'Please try customizing the millage rates again from a candidate.'
    );
  }

  // Apply overrides to the base map
  const customMillageMap = applyMillageOverrides(baseMillageMap, millageOverrides);

  // Calculate summary statistics from the custom map
  const millageValues = Object.values(customMillageMap);
  if (millageValues.length === 0) {
    throw new Error('Custom millage map is empty after applying overrides');
  }

  const peakMillage = Math.max(...millageValues);
  const averageMillage =
    millageValues.reduce((sum, millage) => sum + millage, 0) / millageValues.length;
  const millageUtilization = averageMillage / peakMillage;

  // Placeholder revenue for MillageInformation structure. Actual revenue calculations
  // happen in the revenue engine using real taxable values from the project.
  // This value is only used for display/comparison in millage shape selection UI.
  const totalRevenue = millageValues.reduce((sum, millage) => sum + millage * 1000000, 0);

  // Create intervals for the custom shape
  const sortedYears = Object.keys(customMillageMap)
    .map(Number)
    .sort((a, b) => a - b);
  const intervals = [
    {
      startYear: sortedYears[0],
      endYear: sortedYears[sortedYears.length - 1],
      startMillage: customMillageMap[sortedYears[0]] || 0,
      endMillage: customMillageMap[sortedYears[sortedYears.length - 1]] || 0,
      intervalType: 'linear' as const,
    },
  ];

  // Create custom shape ID and name (sourceShapeName is for display only)
  const sourceShapeName = millageOverrides.sourceShapeName || 'Custom';
  const customShapeId = `custom-millage-${Date.now()}`;

  const millageShape = {
    shapeId: customShapeId,
    shapeName: `${sourceShapeName} (Custom)`,
    shapeType: 'linear' as const,
    intervals,
    adjustments: [],
  };

  return {
    millageShape,
    millageMap: customMillageMap,
    totalRevenue,
    peakMillage,
    averageMillage,
    millageUtilization,
  };
}

/**
 * Builds optimized candidates across all millage shapes
 *
 * ARCHITECTURE:
 * For each shape in shape registry:
 *   1. Generate millage shape
 *   2. Create and process candidate
 *   3. If viable → Optimize immediately
 *   4. Keep only final optimized version
 *   5. Track optimization history
 *
 * SPECIAL CASE: If millage overrides are present, creates a single custom candidate
 * instead of iterating through all shapes.
 *
 * @returns Object containing viable candidates and failed candidates for debugging
 * @throws Error if no candidates can be generated at all
 */
export function buildOptimizedCandidates(
  project: Project,
  configuration: Configuration
): BuildCandidatesResult {
  const startTime = Date.now();
  const baseElements = createBaseProjectElements(project);

  // Check for millage overrides - if present, create single custom candidate
  const millageOverrides = configuration.overrides?.millage;
  if (millageOverrides && Object.keys(millageOverrides.byYear).length > 0) {
    logger.L3.info(`Millage overrides detected - creating single custom candidate`, {
      overrideCount: Object.keys(millageOverrides.byYear).length,
    });

    try {
      const customMillageInfo = createCustomMillageFromOverrides(configuration);

      // Create and process the custom candidate
      let candidate = createCandidateFromMillage(
        project,
        configuration,
        customMillageInfo,
        'SingleTest', // Use SingleTest prefix for custom millage
        'custom-millage' // Custom phase
      );

      let wasRecovered = false;

      // If non-viable, attempt DRF recovery first
      if (!candidate.results.isViable) {
        const recoveryResult = ConfigurationOptimizer.attemptRecovery(candidate, configuration);
        if (recoveryResult.success && recoveryResult.recoveredCandidate) {
          candidate = recoveryResult.recoveredCandidate;
          wasRecovered = true;
          logger.L3.info(`Custom millage candidate recovered from negative DRF`, {
            iterations: recoveryResult.iterationsPerformed,
          });
        }
      }

      // Optimize the candidate (if viable or recovered)
      let finalCandidate = candidate;
      let wasOptimized = false;

      if (candidate.results.isViable) {
        const optimizationResult = ConfigurationOptimizer.runOptimizationPipeline(
          candidate,
          project,
          configuration
        );

        // SAFEGUARD: Only use optimized candidate if it's still viable
        // Optimization can sometimes break a recovered candidate
        if (optimizationResult.success && optimizationResult.finalCandidate.results?.isViable) {
          finalCandidate = optimizationResult.finalCandidate;
          wasOptimized = true;
        } else if (candidate.results.isViable) {
          // Optimization broke viability, keep pre-optimization candidate
          finalCandidate = candidate;
          wasOptimized = false;
          logger.L3.warn(
            'Custom millage optimization broke viability, keeping pre-optimization candidate'
          );
        }
      }

      const processingTime = Date.now() - startTime;

      logger.L3.info(`Custom millage candidate created`, {
        viable: finalCandidate.results.isViable,
        recovered: wasRecovered,
        optimized: wasOptimized,
        processingTime: `${processingTime}ms`,
      });

      return {
        candidates: finalCandidate.results.isViable ? [finalCandidate] : [],
        failedCandidates: finalCandidate.results.isViable ? [] : [finalCandidate],
      };
    } catch (error) {
      const processingTime = Date.now() - startTime;
      console.error(`Failed to create custom millage candidate`, {
        error: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        processingTime: `${processingTime}ms`,
        sourceShapeId: configuration.overrides!.millage!.sourceShapeId,
      });

      return {
        candidates: [],
        failedCandidates: [],
      };
    }
  }

  // Create simplified project data for millage processing
  const projectData = {
    dateRecords: baseElements.dateRecords,
    dateRecordsMapByPaymentDate: baseElements.dateRecordsMapByPaymentDate,
    taxableValueMap: baseElements.taxableValueMap,
    dateInputs: project.dateInputs
      ? {
          startingTaxYear: parseInt(project.dateInputs.startingTaxYear, 10),
        }
      : undefined,
  };

  // Get ALL shapes from registry (53 total)
  const allShapeIds = getAllShapeIds();
  logger.L3.info(`Starting shape→optimize pipeline`, {
    totalShapes: allShapeIds.length,
    bondSeriesCount: configuration.bondSeries.length,
  });

  const optimizedCandidates: Candidate[] = [];
  const nonViableCandidates: Candidate[] = [];
  let shapesProcessed = 0;
  let shapesSkipped = 0;
  let optimizationsApplied = 0;
  let recoveriesAttempted = 0;
  let recoveriesSuccessful = 0;

  for (const shapeId of allShapeIds) {
    try {
      shapesProcessed++;

      // 1. Generate millage shape
      const millageInfo = generateSpecificShape(projectData, configuration, shapeId);

      // Validate millageInfo before proceeding
      if (!millageInfo || !millageInfo.millageShape) {
        logger.L3.error(`Failed to generate millage shape for ${shapeId}: invalid structure`);
        shapesSkipped++;
        continue;
      }

      // 2. Create and process candidate (automatically processed via CandidateProcessor)
      const candidate = createCandidateFromMillage(
        project,
        configuration,
        millageInfo,
        'ShapeOpt', // Name prefix for optimized shapes
        'shape-optimization' // Processing phase
      );

      // 3. Handle non-viable candidates - attempt DRF recovery first
      if (!candidate.results.isViable) {
        // Attempt DRF recovery for candidates with negative DRF balance
        recoveriesAttempted++;
        const recoveryResult = ConfigurationOptimizer.attemptRecovery(candidate, configuration);

        if (recoveryResult.success && recoveryResult.recoveredCandidate) {
          recoveriesSuccessful++;
          logger.L3.info(`DRF recovery successful for shape ${shapeId}`, {
            iterations: recoveryResult.iterationsPerformed,
            adjustments: recoveryResult.adjustments.length,
          });

          // IMPORTANT: Deep clone the recovered candidate before optimization
          // The optimization pipeline may create test candidates that share array references
          // which can pollute the original candidate's failureMessages
          const recoveredCandidate = deepCloneCandidate(recoveryResult.recoveredCandidate);

          // Now optimize the recovered candidate (using clone)
          const optimizationResult = ConfigurationOptimizer.runOptimizationPipeline(
            recoveredCandidate,
            project,
            configuration
          );

          // SAFEGUARD: If optimization broke the recovered candidate, use the pre-optimization version
          // This can happen if utilization optimization adds too much principal back
          let finalCandidate: Candidate;
          if (optimizationResult.success && optimizationResult.finalCandidate.results?.isViable) {
            finalCandidate = optimizationResult.finalCandidate;
          } else if (recoveryResult.recoveredCandidate.results?.isViable) {
            // Optimization broke it, fall back to recovered candidate
            finalCandidate = recoveryResult.recoveredCandidate;
          } else {
            // Neither is viable, use whatever we have (shouldn't happen but be defensive)
            finalCandidate = optimizationResult.finalCandidate;
          }

          if (optimizationResult.optimizationsApplied.length > 0) {
            optimizationsApplied++;
          }

          optimizedCandidates.push(finalCandidate);
          logger.L3.debug(`Recovered and processed shape ${shapeId}`, {
            recovered: true,
            optimized: optimizationResult.success,
            totalPrincipal: finalCandidate.results.totalPrincipal,
          });
          continue;
        }

        // Recovery failed - add to non-viable as before
        shapesSkipped++;
        nonViableCandidates.push(candidate);
        logger.L3.debug(`Collected non-viable shape: ${shapeId}`, {
          failureMessages: candidate.results.failureMessages,
          recoveryAttempted: true,
          recoveryReason: recoveryResult.description,
        });
        continue;
      }

      // 4. Optimize viable candidate
      const optimizationResult = ConfigurationOptimizer.runOptimizationPipeline(
        candidate,
        project,
        configuration
      );

      // 5. Use optimized if successful, otherwise keep original viable candidate
      const finalCandidate = optimizationResult.success
        ? optimizationResult.finalCandidate
        : candidate;

      // 6. Track optimization history (if optimizations were applied)
      if (optimizationResult.optimizationsApplied.length > 0) {
        optimizationsApplied++;

        // Add to optimization history (if not already tracked by ConfigurationOptimizer)
        if (
          !finalCandidate.optimizationHistory ||
          finalCandidate.optimizationHistory.length === 0
        ) {
          finalCandidate.optimizationHistory = optimizationResult.optimizationsApplied.map(
            (type) => {
              const analysis = optimizationResult.analyses.find((a) => a.type === type);
              return {
                type: mapOptimizationTypeToHistoryType(type),
                description: `Applied ${type} optimization${analysis?.reason ? `: ${analysis.reason}` : ''}`,
                timestamp: new Date().toISOString(),
                details: {
                  originalShape: shapeId,
                  estimatedImpact: analysis?.estimatedImpact,
                },
              };
            }
          );
        }
      }

      // 7. Add to results
      optimizedCandidates.push(finalCandidate);

      logger.L3.debug(`Processed shape ${shapeId}`, {
        viable: true,
        optimized: optimizationResult.success,
        optimizationsApplied: optimizationResult.optimizationsApplied,
        totalPrincipal: finalCandidate.results.totalPrincipal,
        efficiency: finalCandidate.results.efficiency,
      });
    } catch (error) {
      logger.error(
        `Failed processing shape ${shapeId}`,
        error instanceof Error ? error : undefined
      );
      // Continue to next shape (don't fail entire pipeline)
    }
  }

  const processingTime = Date.now() - startTime;

  logger.L3.info(`Shape→optimize pipeline complete`, {
    totalShapes: allShapeIds.length,
    shapesProcessed,
    shapesSkipped,
    viableCandidates: optimizedCandidates.length,
    optimizationsApplied,
    recoveriesAttempted,
    recoveriesSuccessful,
    processingTime: `${processingTime}ms`,
  });

  // Rank all non-viable candidates by closeness to viable (for super admin debugging)
  const rankedNonViable = rankNonViableCandidates(nonViableCandidates, configuration);

  // Handle zero viable candidates - return "closest to viable" non-viable candidates
  if (optimizedCandidates.length === 0) {
    logger.L3.warn(
      `No viable candidates found. Returning ${Math.min(MAX_NON_VIABLE_TO_SHOW, nonViableCandidates.length)} closest-to-viable candidates.`
    );

    // Return top N closest-to-viable candidates as main candidates
    const closestToViable = rankedNonViable.slice(0, MAX_NON_VIABLE_TO_SHOW);

    if (closestToViable.length === 0) {
      // No candidates at all - this shouldn't happen but handle gracefully
      throw new Error(
        'Unable to generate any bond structures. This may indicate an issue with the input data.'
      );
    }

    // Exclude candidates that are in the main candidates array from failedCandidates
    // to prevent duplicates (same candidate appearing in both arrays)
    const closestToViableIds = new Set(closestToViable.map((c) => c.id));
    const failedCandidates = rankedNonViable.filter((c) => !closestToViableIds.has(c.id));

    return {
      candidates: closestToViable,
      failedCandidates,
    };
  }

  // Normal case: we have viable candidates, so all non-viable go to failedCandidates
  const failedCandidates = rankedNonViable;

  // Sort: viable first (all should be viable), then by principal, then efficiency
  optimizedCandidates.sort((a, b) => {
    if (a.results.isViable && !b.results.isViable) return -1;
    if (!a.results.isViable && b.results.isViable) return 1;
    if (a.results.totalPrincipal !== b.results.totalPrincipal) {
      return b.results.totalPrincipal - a.results.totalPrincipal;
    }
    return b.results.efficiency - a.results.efficiency;
  });

  return {
    candidates: optimizedCandidates,
    failedCandidates,
  };
}

/**
 * Ranks non-viable candidates by "closeness to viable"
 *
 * The "best" non-viable candidate is the one that came closest to being viable.
 * We measure this by the minimum DRF balance - the least negative value is closest to viable.
 *
 * @param candidates - Array of non-viable candidates
 * @param configuration - Configuration for DRF statistics
 * @returns Sorted array with closest-to-viable candidates first
 */
function rankNonViableCandidates(
  candidates: Candidate[],
  configuration: Configuration
): Candidate[] {
  if (candidates.length === 0) return [];

  // Calculate "closeness score" for each candidate
  const withScores = candidates.map((candidate) => {
    const drfStats = CashFlowCalculator.getDRFStatistics(candidate, configuration);

    // Primary score: lowest DRF balance (higher is better, i.e., less negative)
    // A candidate with lowestBalance of -10,000 is better than one with -100,000
    const closenessScore = drfStats.lowestBalance;

    // Secondary: fewer negative years is better
    const negativeYearsPenalty = drfStats.negativeBalanceYears.length * -10000;

    return {
      candidate,
      closenessScore,
      negativeYearsPenalty,
      totalScore: closenessScore + negativeYearsPenalty,
      drfStats,
    };
  });

  // Sort by total score (highest/least negative first)
  withScores.sort((a, b) => b.totalScore - a.totalScore);

  logger.L3.debug('Non-viable candidates ranked by closeness to viable:', {
    rankings: withScores.slice(0, 5).map((item, idx) => ({
      rank: idx + 1,
      name: item.candidate.name,
      lowestDRF: item.drfStats.lowestBalance,
      negativeYears: item.drfStats.negativeBalanceYears,
      totalScore: item.totalScore,
    })),
  });

  return withScores.map((item) => item.candidate);
}
